/**
 * Table Button Component
 * Button with table builder modal for inserting LaTeX tables
 */

import React, { useState, useEffect } from 'react'
import { EditorView } from '@codemirror/view'
import { TableCellsIcon, XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { getSelection } from '@/utils/latex/insertionHelpers'
import { EditorSelection } from '@codemirror/state'
import AITableGenerator from './AITableGenerator'

interface TableButtonProps {
  view: EditorView | null
  theme?: 'light' | 'dark'
  documentId?: string
}

const TableButton: React.FC<TableButtonProps> = ({ view, theme = 'light', documentId }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isAIGeneratorOpen, setIsAIGeneratorOpen] = useState(false)
  const [rows, setRows] = useState(3)
  const [cols, setCols] = useState(3)
  const [hasBorders, setHasBorders] = useState(true)
  const [alignment, setAlignment] = useState<'left' | 'center' | 'right'>('center')
  const [caption, setCaption] = useState('')
  const [label, setLabel] = useState('')

  useEffect(() => {
    if (isDialogOpen) {
      // Reset to defaults when opening
      setRows(3)
      setCols(3)
      setHasBorders(true)
      setAlignment('center')
      setCaption('')
      setLabel('')
    }
  }, [isDialogOpen])

  const generateTable = () => {
    const alignChar = alignment === 'left' ? 'l' : alignment === 'right' ? 'r' : 'c'
    const border = hasBorders ? '|' : ''
    const colSpec = border + alignChar.repeat(cols) + border
    
    let table = '\\begin{table}[h]\n'
    table += '  \\centering\n'
    table += '  \\begin{tabular}{' + colSpec + '}\n'
    
    if (hasBorders) {
      table += '    \\hline\n'
    }
    
    // Generate rows
    for (let i = 0; i < rows; i++) {
      const cells = Array(cols).fill('').map((_, j) => `Cell ${i + 1}-${j + 1}`)
      table += '    ' + cells.join(' & ') + ' \\\\\n'
      if (hasBorders) {
        table += '    \\hline\n'
      }
    }
    
    table += '  \\end{tabular}\n'
    
    if (caption) {
      table += `  \\caption{${caption}}\n`
    }
    
    if (label) {
      table += `  \\label{${label}}\n`
    }
    
    table += '\\end{table}'
    
    return table
  }

  const handleInsert = () => {
    if (!view) return

    const selection = getSelection(view)
    const tableCode = generateTable()
    const cursorPos = selection.from + tableCode.length

    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: tableCode
      },
      selection: {
        anchor: cursorPos
      }
    })

    view.focus()
    setIsDialogOpen(false)
  }

  const handleAIInsert = (latexCode: string) => {
    if (!view) return

    const selection = getSelection(view)
    const cursorPos = selection.from + latexCode.length

    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: latexCode + '\n\n'
      },
      selection: {
        anchor: cursorPos + 2
      }
    })

    view.focus()
  }

  const buttonClass = clsx(
    'p-1.5 rounded transition-colors',
    theme === 'dark'
      ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
      : 'text-gray-800 hover:bg-gray-100 hover:text-gray-900',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  )

  const dialogClass = clsx(
    'fixed inset-0 z-50 flex items-center justify-center',
    theme === 'dark' ? 'bg-black/50' : 'bg-black/30'
  )

  const contentClass = clsx(
    'relative rounded-lg shadow-xl p-6 w-full max-w-lg',
    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
  )

  const inputClass = clsx(
    'w-full px-3 py-2 rounded-md border text-sm',
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
  )

  return (
    <>
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          disabled={!view}
          className={buttonClass}
        title="Insert table - Create a LaTeX table with custom rows, columns, and formatting"
        aria-label="Insert table"
        >
          <TableCellsIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => setIsAIGeneratorOpen(true)}
          disabled={!view}
          className={buttonClass}
          title="AI: Generate table from natural language description, CSV/TSV data, or image"
          aria-label="AI table generator"
        >
          <SparklesIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {isDialogOpen && (
        <div className={dialogClass} onClick={() => setIsDialogOpen(false)}>
          <div className={contentClass} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsDialogOpen(false)}
              className={clsx(
                'absolute top-4 right-4 p-1 rounded-md',
                theme === 'dark' ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
              )}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <h3 className={clsx('text-lg font-semibold mb-4', theme === 'dark' ? 'text-gray-100' : 'text-gray-900')}>
              Insert Table
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                    Rows
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={rows}
                    onChange={(e) => setRows(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                    Columns
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={cols}
                    onChange={(e) => setCols(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={clsx('block text-sm font-medium mb-2', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                  Alignment
                </label>
                <div className="flex gap-2">
                  {(['left', 'center', 'right'] as const).map((align) => (
                    <button
                      key={align}
                      onClick={() => setAlignment(align)}
                      className={clsx(
                        'flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                        alignment === align
                          ? 'bg-primary-600 text-white'
                          : theme === 'dark'
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      )}
                    >
                      {align.charAt(0).toUpperCase() + align.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="table-borders"
                  checked={hasBorders}
                  onChange={(e) => setHasBorders(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="table-borders" className={clsx('text-sm', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                  Show borders
                </label>
              </div>

              <div>
                <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                  Caption (optional)
                </label>
                <input
                  type="text"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Table caption"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                  Label (optional)
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="tab:mylabel"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsDialogOpen(false)}
                className={clsx('px-4 py-2 rounded-md text-sm', theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100')}
              >
                Cancel
              </button>
              <button
                onClick={handleInsert}
                className="px-4 py-2 rounded-md text-sm bg-primary-600 text-white hover:bg-primary-700"
              >
                Insert Table
              </button>
            </div>
          </div>
        </div>
      )}

      {isAIGeneratorOpen && (
        <AITableGenerator
          view={view}
          theme={theme}
          documentId={documentId}
          onClose={() => setIsAIGeneratorOpen(false)}
          onInsert={handleAIInsert}
        />
      )}
    </>
  )
}

export default TableButton

